export const ERROR_MESSAGE_MAP: Record<string, string> = {
  // System-wide
  ERR_SERVER_500: 'Something went wrong. Please try again. If the problem persists, contact support.',
  ERR_UNAUTHORIZED: 'You do not have permission to access this page.',
  ERR_NOT_FOUND: 'The requested record could not be found.',
  ERR_API_FETCH_FAIL: 'Unable to load data. Please refresh and try again.',
  ERR_DELETE_CONFIRM: 'Are you sure you want to delete this record? This action cannot be undone.',
  ERR_DELETE_PORTAL_FAIL: 'This portal cannot be deleted while active records are associated to it.',

  // Generic UI states
  STATE_NO_DATA: 'No data available in table',
  STATE_EMPTY_TABLE: 'No data available in table',
  STATE_INFO: 'Showing {from} to {to} of {total} entries',
  STATE_PAGINATION: 'Showing {from} to {to} of {total} entries',

  // Authentication and password
  ERR_LOGIN_INVALID: 'Invalid email or password. Please try again.',
  ERR_ACCOUNT_DISABLED: 'Account is disabled.',
  ERR_SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  ERR_TOO_MANY_ATTEMPTS: 'Too many failed login attempts. If you want to continue logging in with this account, please wait another 15 minutes.',
  ERR_PWD_MISMATCH: 'Passwords do not match. Please re-enter.',
  ERR_PWD_CURRENT_REQ: 'Please enter your current password to proceed.',
  ERR_PWD_INCORRECT: 'The current password you entered is incorrect.',
  ERR_RESET_GENERATED: 'A new password has been generated. Please provide the updated credentials to the user directly.',

  // User and administrator validation
  ERR_PORTAL_REQ: 'Please select a portal.',
  ERR_ROLE_REQ: 'Please select a user role.',
  ERR_FNAME_REQ: 'This field is required.',
  ERR_LNAME_REQ: 'This field is required.',
  ERR_EMAIL_REQ: 'This field is required.',
  ERR_EMAIL_FMT: 'Please enter a valid email address.',
  ERR_DUPLICATE_EMAIL: 'An account with this email address already exists.',
  ERR_PWD_REQ: 'This field is required.',
  ERR_PWD_WEAK: 'Password is too weak. Please use a mix of uppercase, lowercase, numbers, and symbols.',

  // Product and portal validation
  ERR_NAME_REQ: 'This field is required.',
  ERR_URL_REQ: 'This field is required.',
  ERR_URL_FMT: 'Please enter a valid URL.',
  ERR_COLOR_REQ: 'Style colour is required.',
  ERR_IMG_DIM: 'Image height must be between 28px and 36px.',
  ERR_DUPLICATE_NAME: 'A portal with this name already exists',
  ERR_DUPLICATE_SKU: 'Product with this SKU already exists',
  STATE_PUBLIC_READONLY: 'The Public setting cannot be changed after the portal has been created.',
  ERR_SKU_REQ: 'This field is required.',

  // Resource management
  ERR_DESC_REQ: 'This field is required.',
  ERR_FILE_MISSING: 'A file is required to create this resource.',
  ERR_TYPE_CHANGE: 'Changing the resource type will clear the uploaded file. Please re-upload a compatible file.',
  ERR_HIRES_REQ: 'Please upload a High-Resolution image.',
  ERR_SEQ_INVALID: 'Sequence must be a valid integer.',
  ERR_STATUS_REQ: 'Please select a valid status.',
  ERR_TITLE_REQ: 'This field is required.',
  ERR_VERSION_REQ: 'Version number is required.',
  ERR_DATE_REQ: 'Release date and time are required.',
  ERR_DATE_FMT: 'Please enter date in dd/mm/yyyy format.',
  ERR_CHECKSUM_REQ: 'Checksum is required.',
  ERR_FILE_REQ: 'Please upload the firmware binary.',
  ERR_SDK_FILE_REQ: 'Please upload an SDK file.',
  ERR_EMU_FILE: 'The selected file type is not supported. Please upload a compatible emulator file.',

  // Certification and maintenance
  ERR_COUNTRY_REQ: 'Please select a country.',
  ERR_PROD_REQ: 'Please select at least one product for certification.',
  ERR_CODE_REQ: 'This field is required.',
  ERR_PREF_REQ: 'This field is required.',
  ERR_SUBJ_REQ: 'This field is required.',
  ERR_MSG_REQ: 'Email message body is required.',
  ERR_DUPLICATE_KEY: 'A country with this name already exists. Please enter a unique country name.',
  ERR_DUPLICATE_CAT: 'A category with this name already exists. Please enter a unique name.',
  ERR_DUPLICATE_CODE: 'A template with this code already exists. Please enter a unique code.',
  ERR_DELETE_FAIL: 'This country cannot be deleted while it is associated with active records.',
  ERR_CAT_IN_USE: 'This category cannot be deleted while it is associated with existing resources.',
  ERR_GROUP_HAS_PRODUCTS: 'This group has products assigned and cannot be deleted. Reassign or remove products first.',
  ERR_GROUP_DEACTIVATE_CONFIRM: 'Deactivating this group will remove it from all filter dropdowns. Products already assigned to this group will remain assigned. Do you want to continue?',
  ERR_NO_RESULTS: 'No data available in table',
  ERR_NO_DATA: 'No data available in table',
  ERR_NO_TEMPLATES: 'No data available in table',
  ERR_DUPLICATE_CERT_REQUEST: "A certification request already exists for this product/model combination"
};

export const DEFAULT_ERROR_MESSAGE = ERROR_MESSAGE_MAP['ERR_SERVER_500'];

export const INLINE_ERROR_CODES = new Set<string>([
  'ERR_LOGIN_INVALID',
  'ERR_NAME_REQ',
  'ERR_DESC_REQ',
  'ERR_CODE_REQ',
  'ERR_PREF_REQ',
  'ERR_SUBJ_REQ',
  'ERR_MSG_REQ',
  'ERR_PORTAL_REQ',
  'ERR_COUNTRY_REQ',
  'ERR_PROD_REQ',
  'ERR_DATE_FMT',
  'ERR_DATE_REQ',
  'ERR_SEQ_INVALID',
  'ERR_URL_REQ',
  'ERR_URL_FMT',
  'ERR_COLOR_REQ',
  'ERR_IMG_DIM',
  'ERR_SKU_REQ',
  'ERR_ROLE_REQ',
  'ERR_FNAME_REQ',
  'ERR_LNAME_REQ',
  'ERR_EMAIL_REQ',
  'ERR_EMAIL_FMT',
  'ERR_PWD_REQ',
  'ERR_PWD_WEAK',
  'ERR_PWD_CURRENT_REQ',
  'ERR_PWD_INCORRECT',
  'ERR_PWD_MISMATCH',
  'ERR_FILE_MISSING',
  'ERR_HIRES_REQ',
  'ERR_STATUS_REQ',
  'ERR_TITLE_REQ',
  'ERR_VERSION_REQ',
  'ERR_CHECKSUM_REQ',
  'ERR_FILE_REQ',
  'ERR_SDK_FILE_REQ',
  'ERR_EMU_FILE'
]);

export const BANNER_ERROR_CODES = new Set<string>([
  'ERR_SERVER_500',
  'ERR_UNAUTHORIZED',
  'ERR_NOT_FOUND',
  'ERR_API_FETCH_FAIL',
  'ERR_DUPLICATE_KEY',
  'ERR_DUPLICATE_CAT',
  'ERR_DUPLICATE_CODE',
  'ERR_DUPLICATE_NAME',
  'ERR_DUPLICATE_SKU',
  'ERR_DUPLICATE_EMAIL',
  'ERR_ACCOUNT_DISABLED',
  'ERR_TOO_MANY_ATTEMPTS',
  'ERR_GROUP_HAS_PRODUCTS'
]);
